import * as z from 'zod';
import { isDigit } from '../utils';

export const REQUIRED_TEXT = '필수 입력 값입니다.';
export const REQUIRED_IMAGE_ONLY = 'png, jpeg, gif 파일만 가능합니다.';

const customErrorMap: z.ZodErrorMap = (error, ctx) => {
  /*
  This is where you override the various error codes
  */
  switch (error.code) {
    case z.ZodIssueCode.too_small:
      if (error.minimum === 1) {
        return { message: REQUIRED_TEXT };
      }
      return { message: `최소 ${error.minimum}자 까지 입력하세요.` };
    case z.ZodIssueCode.too_big:
      return { message: `최대 ${error.maximum}자 까지 입력하세요.` };
    case z.ZodIssueCode.invalid_string:
      if (error.validation === 'email') {
        return { message: `이메일 형식을 확인하세요.` };
      }
      break;
    case z.ZodIssueCode.invalid_type:
      if (error.expected === 'string') {
        return { message: `필수 입력 값입니다.` };
      }
      break;
    case z.ZodIssueCode.custom:
      // produce a custom message using error.params
      // error.params won't be set unless you passed
      // a `params` arguments into a custom validator
      const params = error.params || {};
      if (params.myField) {
        return { message: `Bad input: ${params.myField}` };
      }
      break;
  }

  // fall back to default message!
  return { message: ctx.defaultError };
};

z.setErrorMap(customErrorMap);

const password = z
  .string()
  .min(8, { message: '비밀번호는 최소 8자 이상입니다.' })
  .max(16, { message: '비밀번호는 최대 16자입니다.' })
  .regex(/[a-zA-Z]/, '비밀번호는 영문 대 소문자를 포함해야 합니다.')
  .regex(/[0-9]/, '비밀번호는 숫자를 포함해야 합니다.')
  .regex(/[^\w]/, '비밀번호는 특수문자를 포함해야 합니다.');
// .nullable()
// .optional()
// .or(z.literal(''));

// 한국 기관 도메인의 2단계 라벨에서 점이 빠진 흔한 오타.
// 예) chosun.ackr → chosun.ac.kr, foo.cokr → foo.co.kr, bar.orkr → bar.or.kr
const KR_DOMAIN_TYPO = /\.(ac|co|or|go|re|ne|pe|hs|ms|es|sc|kg)kr$/i;

// 흔한 최상위 도메인(TLD) 오타 → 올바른 TLD.
// 문법상 유효하지만 실제로는 존재하지 않는 오타들만 등록한다.
const TLD_TYPOS: Record<string, string> = {
  con: 'com',
  cmo: 'com',
  ocm: 'com',
  vom: 'com',
  xom: 'com',
  comm: 'com',
  coom: 'com',
  cim: 'com',
  nett: 'net',
  nte: 'net',
  orgg: 'org',
  ogr: 'org'
};

// 이메일 도메인이 흔한 오타로 판단되면 교정안을 돌려준다. 아니면 null.
const suggestEmailDomainFix = (value: string): string | null => {
  const domain = (value.split('@')[1] ?? '').toLowerCase();
  if (!domain) return null;

  // 1) 한국 기관 도메인 2단계 라벨에서 점이 빠진 오타 (ac.kr → ackr 등)
  const krMatch = domain.match(KR_DOMAIN_TYPO);
  if (krMatch) {
    return domain.replace(KR_DOMAIN_TYPO, `.${krMatch[1].toLowerCase()}.kr`);
  }

  // 2) 흔한 TLD 오타 (.con → .com 등)
  const lastDot = domain.lastIndexOf('.');
  if (lastDot > 0) {
    const tld = domain.slice(lastDot + 1);
    if (TLD_TYPOS[tld]) {
      return domain.slice(0, lastDot + 1) + TLD_TYPOS[tld];
    }
  }

  return null;
};

const email = z
  .string()
  .trim()
  .email({ message: '이메일 형식을 확인하세요.' })
  .refine((v) => !suggestEmailDomainFix(v), (v) => {
    const domain = (v.split('@')[1] ?? '').toLowerCase();
    const suggestion = suggestEmailDomainFix(v);
    return {
      message: suggestion
        ? `이메일 도메인을 확인하세요. (${domain} → ${suggestion})`
        : '이메일 도메인을 확인하세요.'
    };
  });

const multiselect = z
  .array(
    z.string().or(
      z.object({
        value: z.string(),
        label: z.string()
      })
    )
  )
  .nonempty()
  .transform((values) => {
    return values.map((v) => (typeof v === 'object' ? v.value : v));
  });

const multiselectOptional = z
  .array(
    z.string().or(
      z.object({
        value: z.string(),
        label: z.string()
      })
    )
  )
  .transform((values) => {
    return values.map((v) => (typeof v === 'object' ? v.value : v));
  });

const mobile = z
  .string()
  .trim()
  .regex(
    /^01([0|1|6|7|8|9])-?([0-9]{3,4})-?([0-9]{4})$/,
    '휴대전화번호 형식을 확인해주세요.'
  )
  .transform((v) => {
    return v.replaceAll('-', '');
  });
const mobileOptional = z
  .string()
  .regex(
    /^01([0|1|6|7|8|9])-?([0-9]{3,4})-?([0-9]{4})$/,
    '휴대전화번호 형식을 확인해주세요.'
  )
  .transform((v) => {
    return v ? v.replaceAll('-', '') : v;
  })
  .or(z.literal(''));

const internetPhoneNumber = z
  .string()
  .trim()
  .regex(
    /^070-?([0-9]{3,4})-?([0-9]{4})$/,
    '인터넷 전화 형식을 확인해주세요. ex) 07012341234'
  )
  .transform((v) => {
    return v.replaceAll('-', '');
  });

const bigIntId = z
  .string()
  .refine(
    (v) => isDigit(v),
    (v) => ({ message: `${v} is invalid ID` })
  )
  .transform((v) => {
    return BigInt(v);
  })
  .or(z.bigint());

const phoneNumberAll = z
  .string()
  .trim()
  .regex(
    /^(0(2|3[1-3]|4[1-4]|5[1-5]|6[1-4]|10|16|17|19))-?(\d{3,4})-?(\d{4})$/,
    '전화번호 형식을 확인해주세요.'
  )
  .transform((v) => {
    return v.replaceAll('-', '');
  });

export {
  z as sv,
  email,
  password,
  multiselect,
  multiselectOptional,
  mobile,
  mobileOptional,
  bigIntId,
  internetPhoneNumber,
  phoneNumberAll
};
